import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import carImage from '../../assets/images/car.png';

// Tempo máximo de espera pela sessão de recuperação antes de considerar o link inválido.
const TIMEOUT_SESSAO_MS = 5000;

type StatusLink = 'verificando' | 'pronto' | 'invalido';

export function RedefinirSenha() {
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLink, setStatusLink] = useState<StatusLink>('verificando');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const navigate = useNavigate();
  const { updatePassword, signOut } = useAuth();

  // Verificar se o usuário chegou através de um link de redefinição válido.
  //
  // ATENÇÃO: não olhar window.location.hash aqui. O detectSessionInUrl do
  // supabase-js consome o fragmento e o apaga da URL (history.replaceState) de
  // forma assíncrona, normalmente antes deste efeito rodar — a verificação por
  // hash acusava "link inválido" mesmo em links bons. O que importa é existir
  // uma sessão, então é isso que observamos.
  useEffect(() => {
    let cancelado = false;

    const marcarPronto = () => {
      if (!cancelado) setStatusLink('pronto');
    };

    // 1) Evento: cobre o caso em que o token da URL ainda está sendo processado.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
        marcarPronto();
      }
    });

    // 2) Consulta direta: cobre o caso em que o evento já ocorreu antes da montagem.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) marcarPronto();
    });

    // 3) Sem sessão dentro do prazo, o link não vale.
    const timeout = setTimeout(() => {
      if (!cancelado) {
        setStatusLink((atual) => (atual === 'verificando' ? 'invalido' : atual));
      }
    }, TIMEOUT_SESSAO_MS);

    return () => {
      cancelado = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (statusLink !== 'pronto') {
      return;
    }

    // Validar senhas
    if (senha !== confirmarSenha) {
      setMessage({
        type: 'error',
        text: 'As senhas não coincidem.'
      });
      return;
    }

    if (senha.length < 6) {
      setMessage({
        type: 'error',
        text: 'A senha deve ter pelo menos 6 caracteres.'
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await updatePassword(senha);

      setMessage({
        type: 'success',
        text: 'Senha atualizada com sucesso! Você será redirecionado para o login.'
      });

      // Encerrar a sessão de recuperação: o acesso ao sistema deve ser feito
      // com a senha nova, e não com o token que veio do e-mail.
      try {
        await signOut();
      } catch (erroLogout) {
        console.error('Erro ao encerrar a sessão de recuperação:', erroLogout);
      }

      // Redirecionar para a página de login após 3 segundos
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.message || 'Ocorreu um erro ao redefinir sua senha. Tente novamente ou solicite um novo link.'
      });
    } finally {
      setLoading(false);
    }
  };

  const sucesso = message?.type === 'success';
  const formularioBloqueado = statusLink !== 'pronto' || loading || sucesso;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={carImage} alt="Automóvel" className="mx-auto mb-4" />
          <h1 className="text-1xl font-bold text-gray-800 mb-2">Redefinição de Senha</h1>
          <p className="text-gray-600">
            {statusLink === 'verificando' ? 'Validando seu link...' : 'Digite sua nova senha'}
          </p>
        </div>

        {statusLink === 'verificando' && (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {statusLink === 'invalido' && (
          <div className="space-y-6">
            <div className="p-3 rounded-md text-sm bg-red-50 text-red-500">
              Link de redefinição inválido ou expirado. Solicite um novo link.
            </div>
            <Link
              to="/esqueceu-senha"
              className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Solicitar novo link
            </Link>
          </div>
        )}

        {statusLink === 'pronto' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {message && (
              <div className={`p-3 rounded-md text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-600'
                  : 'bg-red-50 text-red-500'
              }`}>
                {message.text}
              </div>
            )}

            <Input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Nova senha"
              icon={<Lock className="w-5 h-5" />}
              disabled={formularioBloqueado}
              required
            />

            <Input
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              placeholder="Confirme a nova senha"
              icon={<Lock className="w-5 h-5" />}
              disabled={formularioBloqueado}
              required
            />

            <Button
              type="submit"
              disabled={formularioBloqueado}
              isLoading={loading}
              className="w-full"
            >
              Redefinir Senha
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          <Link to="/" className="text-blue-600 hover:text-blue-500 font-medium">
            Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
